// SignUpDetailsFormContent.tsx
import React from 'react'
import Box from '@mui/material/Box'
import NextImage from 'next/image'
import { Trans, useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import useEnterKey from '@/hooks/useEnterKey'
import { Constants } from '@/assets'

import { EmtButton, EmtCheckbox, EmtTextField, EmtTypography } from 'emt-components-v2'
import NextMuiLink from '@/components/NextMuiLink'
import LinkButton from '@/components/EMT/LinkButton'
import AddressAutocomplete from '@/components/EMT/AddressAutocomplete'
import EmtPhoneInput from '@/components/EmtPhoneInput'
import { Routes } from '@/assets/configs'

import { useSignUpDetailsForm } from '@/hooks/SignUp/useSignUpDetailsForm'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import VariantImages from '@/assets/VariantImages'
import { PartnerOptins } from '@/modules/User/apiTypes'

type Props = {
  type: string
  fakeAnimal?: boolean
  color?: string
  onNext?: () => void
  buttonSticky?: boolean
  isBelgium?: boolean
  isFrance?: boolean
  detailsPage?: boolean
}

export default function SignUpDetailsFormContent({
  type,
  fakeAnimal = false,
  color = 'primary',
  onNext,
  buttonSticky = false,
  isBelgium = false,
  isFrance = false,
  detailsPage = false,
}: Props) {
  const addressRef = React.useRef<HTMLDivElement | null>(null)
  const phoneRef = React.useRef<HTMLDivElement | null>(null)
  const purinaBeOptinRef = React.useRef<HTMLDivElement | null>(null)
  const purinaFrOptinRef = React.useRef<HTMLDivElement | null>(null)
  const ultraPremiumOptinRef = React.useRef<HTMLDivElement | null>(null)
  const isOfAgeRef = React.useRef<HTMLDivElement | null>(null)
  const answerRequestsRef = React.useRef<HTMLDivElement | null>(null)
  const [submitAttempt, setSubmitAttempt] = React.useState(0)
  const { t } = useTranslation('connection')
  const { t: tc } = useTranslation('common')
  const [enterKeyRefClick] = useEnterKey()
  const { isPetsittingMode } = usePetsittingMode()
  const router = useRouter()
  const showPurinaContestBe = router.query.lang === 'be' || isBelgium
  const showPurinaContestFr = router.query.lang === 'fr' || isFrance

  const {
    isMobile,
    rowGap,
    isFocus,
    handleIsFocus,
    isQueryOwner,
    values,
    errors,
    setError,
    isValid,
    onFieldChange,
    onSignUp,
    isLoading,
    // renderOptins,
  } = useSignUpDetailsForm(type)

  const getPartnerOptinsInput = (): PartnerOptins.UserPartnerOptinInput[] => {
    const optins: PartnerOptins.UserPartnerOptinInput[] = []

    if (showPurinaContestBe && values.purinaBeOptin) {
      optins.push({
        optinSlug: 'purina_be',
        optinText: t('sign-up.details.purinaContest.checkbox') as string,
        freeField: values.purinaBeSentence || '',
        frontMetadata: 'signup_details',
      })
    }

    if (showPurinaContestFr && values.purinaFrOptin) {
      optins.push({
        optinSlug: 'purina_fr',
        optinText: 'J accepte de recevoir des communications Purina France.',
        frontMetadata: 'signup_details',
      })
    }

    if (values.ultraPremiumOptin) {
      optins.push({
        optinSlug: 'ultra_premium',
        optinText: 'Je souhaite recevoir les offres partenaires ultra premium.',
        frontMetadata: 'signup_details',
      })
    }

    return optins
  }

  React.useEffect(() => {
    if (!submitAttempt) return

    const orderedFields: string[] = ['firstName', 'lastName', 'email', 'password', 'plainPassword']

    if (isQueryOwner) {
      orderedFields.push('address', 'phone')
    }

    if (showPurinaContestBe) {
      orderedFields.push('purinaBeSentence', 'purinaBeOptin')
    }

    if (showPurinaContestFr && detailsPage) {
      orderedFields.push('purinaFrOptin')
    }

    if (detailsPage) {
      orderedFields.push('ultraPremiumOptin')
    }

    orderedFields.push('isOfAge')

    if (type === 'pro') {
      orderedFields.push('answerRequests')
    }

    const firstErrorField = orderedFields.find(
      (field) => !!(errors as Record<string, string | undefined>)[field]?.length,
    )
    if (!firstErrorField) return

    const refByField: Record<string, React.RefObject<HTMLDivElement>> = {
      address: addressRef,
      phone: phoneRef,
      purinaBeOptin: purinaBeOptinRef,
      purinaFrOptin: purinaFrOptinRef,
      ultraPremiumOptin: ultraPremiumOptinRef,
      isOfAge: isOfAgeRef,
      answerRequests: answerRequestsRef,
    }

    if (refByField[firstErrorField]?.current) {
      refByField[firstErrorField].current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [detailsPage, errors, isQueryOwner, showPurinaContestBe, showPurinaContestFr, submitAttempt, type])

  const submit = (shouldGoNext = false) => {
    setSubmitAttempt((previous) => previous + 1)
    onSignUp(fakeAnimal, getPartnerOptinsInput())
    if (shouldGoNext) onNext && onNext()
  }

  const renderTextField = (key: any, inputType: string) => (
    <EmtTextField
      fullWidth
      id={key}
      type={inputType}
      label={t(`sign-up.details.labels.${key}`)}
      value={(values as any)[key]}
      onFocus={() => handleIsFocus(true)}
      onBlur={() => handleIsFocus(false)}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFieldChange(key, e.target.value)}
      error={!!(errors as any)[key]?.length}
      helperText={(errors as any)[key]}
      onKeyDown={(e) => e.key === 'Enter' && submit()}
    />
  )

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: rowGap }}>
        {renderTextField('firstName', 'text')}
        {renderTextField('lastName', 'text')}
      </Box>

      {renderTextField('email', 'email')}

      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: rowGap }}>
        {renderTextField('password', 'password')}
        {renderTextField('plainPassword', 'password')}
      </Box>

      {isQueryOwner && (
        <>
          {!fakeAnimal && (
            <Box>
              <EmtTypography variant="body1" sx={{ fontWeight: 'bold' }}>
                {t('sign-up.details.contact.title')}
              </EmtTypography>
              <EmtTypography variant="body2">{t('sign-up.details.contact.subtitle')}</EmtTypography>
            </Box>
          )}

          <Box ref={addressRef}>
            <AddressAutocomplete
              label={t('create-your-profile.address-photo.labels.address')}
              address={values.address}
              onAddressChange={(address) => onFieldChange('address', address)}
              addressError={errors.address}
              onFocus={() => handleIsFocus(true)}
              onBlur={() => handleIsFocus(false)}
              onAddressError={(value) => {
                if (!values.address) setError('address', value)
              }}
              strict={true}
            />
          </Box>

          <Box ref={phoneRef}>
            <EmtPhoneInput
              error={errors.phone?.length ? errors.phone : undefined}
              onChange={(phoneNumber) => onFieldChange('phone', phoneNumber)}
              defaultValue={values.phone}
              value={values.phone}
              onFocus={() => handleIsFocus(true)}
              onBlur={() => handleIsFocus(false)}
            />
          </Box>
        </>
      )}

      {(Constants.brand === 'emt' || Constants.brand === 'sst') && showPurinaContestBe && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            padding: '20px 12px',
            border: `2px solid ${isPetsittingMode || type === 'pro' ? '#AE59C6' : '#D9293E'}`,
            borderRadius: '25px',
            margin: '0 -12px',
          }}
        >
          <NextImage
            src={
              isPetsittingMode || type === 'pro'
                ? isMobile
                  ? VariantImages.PurinaBannerMobileSST
                  : VariantImages.PurinaBannerSST
                : isMobile
                ? VariantImages.PurinaBannerMobile
                : VariantImages.PurinaBanner
            }
            quality={100}
            alt=""
            style={{ width: '100%', height: 'auto' }}
          />
          <EmtTypography variant="body2" fontWeight="600" sx={{}}>
            {t('sign-up.details.purinaContest.description')}
          </EmtTypography>
          <Box>
            <EmtTypography variant="body2" fontWeight="800" sx={{}}>
              {t('sign-up.details.purinaContest.question')}
            </EmtTypography>
            <EmtTypography variant="caption" color="textSecondary" sx={{}}>
              {t('sign-up.details.purinaContest.tips')}
            </EmtTypography>
          </Box>
          <Box>
            <EmtTextField
              fullWidth
              id={'purinaBeSentence'}
              type={'number'}
              label={t(`sign-up.details.labels.purinaContest`) as string}
              placeholder={t(`sign-up.details.purinaContest.placeholder`) as string}
              value={values.purinaBeSentence}
              onFocus={() => handleIsFocus(true)}
              onBlur={() => handleIsFocus(false)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFieldChange('purinaBeSentence', e.target.value)}
              error={!!errors.purinaBeSentence?.length}
              helperText={errors.purinaBeSentence}
              onKeyDown={(e) => e.key === 'Enter' && onSignUp(fakeAnimal, getPartnerOptinsInput())}
            />

            <Box
              ref={purinaBeOptinRef}
              sx={{
                '& .MuiBox-root': {
                  lineHeight: '1rem',
                  '& span': { lineHeight: '0px' },
                  '& p': { lineHeight: '1rem', display: 'none' },
                },
              }}
            >
              <EmtCheckbox
                label={'label'}
                caption={
                  <Trans
                    t={t}
                    i18nKey={'sign-up.details.purinaContest.checkbox'}
                    components={{
                      Link: (
                        <a
                          href="https://www.nestle.be/fr/info/yourdata"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 'inherit',
                            display: 'inline!important',
                            textDecoration: 'underline',
                            color: '#A5A3A9',
                          }}
                        />
                      ),
                    }}
                  />
                }
                checked={!!values.purinaBeOptin}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFieldChange('purinaBeOptin', e.target.checked)}
                error={!!errors.purinaBeOptin?.length}
                helperText={errors.purinaBeOptin}
                variant={isPetsittingMode || type === 'pro' ? 'purple' : color}
              />
            </Box>
          </Box>
        </Box>
      )}

      {(Constants.brand === 'emt' || Constants.brand === 'sst') && showPurinaContestFr && detailsPage && (
        <Box
          ref={purinaFrOptinRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            '& .MuiBox-root': { lineHeight: '1rem', '& span': { lineHeight: '0px' }, '& p': { lineHeight: '1rem' } },
          }}
        >
          <EmtCheckbox
            label="Des offres et des infos pour vos animaux :"
            caption="Je souhaite recevoir des offres et bons de réduction, ainsi que des conseils sur la nutrition et le bien-être de mon animal de la part de notre partenaire PURINA® France.. J’accepte l’utilisation d’un pixel de suivi dans les emails PURINA® à des fins de personnalisation de leur contenu. Je peux retirer mon consentement à tout moment."
            checked={!!values.purinaFrOptin}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFieldChange('purinaFrOptin', e.target.checked)}
            error={!!errors.purinaFrOptin?.length}
            helperText={errors.purinaFrOptin}
            variant={isPetsittingMode || type === 'pro' ? 'purple' : color}
          />
        </Box>
      )}

      {/* {(Constants.brand === 'emt' || Constants.brand === 'sst') && showPurinaContestFr && detailsPage && (
        <Box
          ref={ultraPremiumOptinRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            '& .MuiBox-root': { lineHeight: '1rem', '& span': { lineHeight: '0px' }, '& p': { lineHeight: '1rem' } },
          }}
        >
          <EmtCheckbox
            caption={
              <>
                Je souhaite recevoir la newsletter personnalisée d&apos;Ultra Premium Direct. Les communications
                d&apos;Ultra Premium Direct incluent des pixels de suivi afin de mesurer l&apos;efficacité des envois et
                adapter le contenu à vos habitudes. En cochant cette case, je consens à recevoir ces e-mails ainsi
                qu&apos;à l&apos;utilisation de ces traceurs. Pour en savoir plus ou exercer vos droits, consultez la{' '}
                <a
                  href="https://www.ultrapremiumdirect.com/page/donnees-personnelles/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 'inherit', textDecoration: 'underline', color: 'inherit' }}
                >
                  politique de confidentialité
                </a>{' '}
                de Ultra Premium Direct.
              </>
            }
            checked={!!values.ultraPremiumOptin}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFieldChange('ultraPremiumOptin', e.target.checked)}
            error={!!errors.ultraPremiumOptin?.length}
            helperText={errors.ultraPremiumOptin}
            variant={isPetsittingMode || type === 'pro' ? 'purple' : color}
          />
        </Box>
      )} */}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Box ref={isOfAgeRef}>
          <EmtCheckbox
            label={t(`sign-up.details.labels.isOfAge.${type}`) as string}
            checked={values.isOfAge}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFieldChange('isOfAge', e.target.checked)}
            error={!!errors.isOfAge?.length}
            helperText={errors.isOfAge}
            variant={isPetsittingMode || type === 'pro' ? 'purple' : color}
          />
        </Box>

        {type === 'pro' && (
          <Box ref={answerRequestsRef}>
            <EmtCheckbox
              label={t(`sign-up.details.labels.answerRequests`) as string}
              checked={!!values.answerRequests}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFieldChange('answerRequests', e.target.checked)}
              error={!!errors.answerRequests?.length}
              helperText={errors.answerRequests}
              variant="purple"
            />
          </Box>
        )}

        {/* {!showPurinaContestBe && renderOptins(type === 'pro' ? 'purple' : color)} */}
      </Box>

      <EmtTypography
        variant="caption"
        sx={{
          color: (theme: any) => theme.palette.greyGradient[350],
          '& a': {
            color: (theme: any) =>
              color === 'purple' || type === 'pro' ? (theme.palette as any).purple.main : theme.palette.primary.main,
            fontWeight: 'bold',
            textDecoration: 'none',
          },
        }}
      >
        {isPetsittingMode ? (
          <Trans
            t={t}
            i18nKey="sign-up.details.acceptTosAndPactFormatPetsitting"
            components={{
              LinkTOS: <NextMuiLink href={tc('footer.linksPetsitting.tos.url')} />,
            }}
          />
        ) : (
          <Trans
            t={t}
            i18nKey="sign-up.details.acceptTosAndPactFormat"
            components={{
              LinkTOS: <NextMuiLink href={tc('footer.links.tos.url')} />,
              LinkPact: <NextMuiLink href={tc('footer.links.pact.url')} />,
            }}
          />
        )}
      </EmtTypography>

      <Box
        sx={{
          display: 'flex',
          position: buttonSticky ? 'sticky' : 'relative',
          bottom: buttonSticky ? '20px' : '0',
          gap: rowGap,
          ...(isMobile
            ? {
                position: fakeAnimal ? 'sticky' : 'fixed',
                bottom: '20px',
                left: '5%',
                width: fakeAnimal ? '100%' : '90%',
                zIndex: 1,
                display: isFocus && isMobile ? 'none' : 'flex',
              }
            : {}),
        }}
        className="EmtReactNativeWebViewPaddingBottom"
      >
        {!fakeAnimal && (
          <LinkButton
            variant="outlined"
            fullWidth
            href={Routes.Connection.SignUp.Type}
            sx={isMobile ? { background: 'white', '&:hover': { background: 'white' } } : {}}
            color={isPetsittingMode || type === 'pro' ? 'purple' : color}
          >
            {t('sign-up.details.back')}
          </LinkButton>
        )}

        <EmtButton
          ref={enterKeyRefClick}
          fullWidth
          onClick={() => submit(true)}
          loader={isLoading}
          disabled={!isValid}
          color={isPetsittingMode || type === 'pro' ? 'purple' : color}
        >
          {fakeAnimal && isQueryOwner ? tc('seePetsitters') : t('sign-up.details.next')}
        </EmtButton>
      </Box>
    </>
  )
}
